import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

import "./HomeWPPHero.css";

/*
 * Cada grupo aparece en tres pasos:
 *
 * divider -> mensaje ZY -> mensaje usuario
 */
const TIMELINE_STEP_CONFIG = [
  {
    dividerStep: 1,
    buddyStep: 2,
    userStep: 3,
  },
  {
    dividerStep: 4,
    buddyStep: 5,
    userStep: 6,
  },
  {
    dividerStep: 7,
    buddyStep: 8,
    userStep: 9,
  },
  {
    dividerStep: 10,
    buddyStep: 11,
    userStep: 12,
  },
];

/*
 * La animación ahora:
 *
 * - empieza cuando el Hero entra en viewport;
 * - es más rápida;
 * - llega al paso 13;
 * - NO vuelve a comenzar.
 */
const LAST_TIMELINE_STEP = 13;

const STEP_DURATIONS = [
  280, // teléfono listo
  120, // divisor mañana
  210, // mensaje ZY
  280, // respuesta

  120,
  210,
  280,

  120,
  210,
  280,

  120,
  210,
  280,
];

const ENTRY_ANIMATION = {
  animation:
    "home-wpp-timeline-enter 420ms cubic-bezier(0.22, 1, 0.36, 1) both",
};

function PhoneStatusBar() {
  return (
    <>
      <img
        className="home-wpp-phone__status-image"
        src="/status-bar-iphone.png"
        alt=""
        aria-hidden="true"
      />

      <span className="home-wpp-phone__dynamic-island" aria-hidden="true" />
    </>
  );
}

function PhoneSideButtons() {
  return (
    <>
      <div
        className="home-wpp-phone__buttons home-wpp-phone__buttons--left"
        aria-hidden="true"
      >
        <i />
        <i />
        <i />
      </div>

      <div
        className="home-wpp-phone__buttons home-wpp-phone__buttons--right"
        aria-hidden="true"
      >
        <i />
      </div>
    </>
  );
}

function ZYBuddyHeader({ assistantName, onlineLabel, backLabel }) {
  return (
    <header className="home-wpp-phone__header">
      <button
        type="button"
        className="home-wpp-phone__back"
        aria-label={backLabel}
        tabIndex={-1}
      >
        <FiChevronLeft />
      </button>

      <div className="home-wpp-phone__avatar">ZY</div>

      <div className="home-wpp-phone__identity">
        <strong>{assistantName}</strong>

        <span>
          <i />
          {onlineLabel}
        </span>
      </div>
    </header>
  );
}

function TimelineDivider({ children }) {
  return (
    <div className="home-wpp-timeline__divider">
      <span />

      <strong>{children}</strong>

      <span />
    </div>
  );
}

function TimelineItem({ item, currentStep }) {
  const showDivider = currentStep >= item.dividerStep;
  const showBuddyMessage = currentStep >= item.buddyStep;
  const showUserMessage = currentStep >= item.userStep;

  if (!showDivider) {
    return null;
  }

  return (
    <div className="home-wpp-timeline__item">
      <TimelineDivider>{item.period}</TimelineDivider>

      <div className="home-wpp-timeline__conversation">
        {showBuddyMessage ? (
          <div
            className="home-wpp-message home-wpp-message--buddy"
            style={ENTRY_ANIMATION}
          >
            {item.buddyMessage}
          </div>
        ) : null}

        {showUserMessage ? (
          <div
            className="home-wpp-message home-wpp-message--user"
            style={ENTRY_ANIMATION}
          >
            {item.userMessage}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ZYBuddyPhone({
  currentStep,
  timelineMessages,
  assistantName,
  onlineLabel,
  backLabel,
  viewButtonLabel,
  phoneLabel,
}) {
  const showViewButton = currentStep >= LAST_TIMELINE_STEP;

  return (
    <div className="home-wpp-phone" role="img" aria-label={phoneLabel}>
      <div className="home-wpp-phone__frame">
        <PhoneSideButtons />

        <div className="home-wpp-phone__screen">
          <PhoneStatusBar />

          <ZYBuddyHeader
            assistantName={assistantName}
            onlineLabel={onlineLabel}
            backLabel={backLabel}
          />

          <main className="home-wpp-chat">
            <div className="home-wpp-timeline">
              {timelineMessages.map((item) => (
                <TimelineItem
                  key={item.id}
                  item={item}
                  currentStep={currentStep}
                />
              ))}
            </div>

            {showViewButton ? (
              <button
                type="button"
                className="home-wpp-chat__view-button"
                tabIndex={-1}
              >
                <span>{viewButtonLabel}</span>

                <FiChevronRight />
              </button>
            ) : null}
          </main>

          <img
            className="home-wpp-phone__bottom-bar-image"
            src="/bottom-bar.png"
            alt=""
            aria-hidden="true"
          />
        </div>
      </div>
    </div>
  );
}

export default function HomeWPPHero() {
  const { t, i18n } = useTranslation("home");

  const sectionRef = useRef(null);

  const [hasEntered, setHasEntered] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const timelineMessages = useMemo(() => {
    const translatedTimeline = t("phone.timeline", {
      returnObjects: true,
    });

    if (!Array.isArray(translatedTimeline)) {
      return [];
    }

    return translatedTimeline.map((item, index) => ({
      ...item,
      ...TIMELINE_STEP_CONFIG[index],
    }));
  }, [t]);

  /*
   * Empezamos la secuencia cuando realmente
   * aparece el Hero en pantalla.
   *
   * El observer se desconecta después:
   * no vuelve a dispararse al hacer scroll.
   */
  useEffect(() => {
    const section = sectionRef.current;

    if (!section) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          return;
        }

        setHasEntered(true);
        observer.disconnect();
      },
      {
        threshold: 0.3,
      },
    );

    observer.observe(section);

    return () => {
      observer.disconnect();
    };
  }, []);

  /*
   * Avanzamos solamente hasta LAST_TIMELINE_STEP.
   *
   * Cuando llega al final no hay ningún timeout nuevo,
   * así que todo queda visible.
   */
  useEffect(() => {
    if (!hasEntered || currentStep >= LAST_TIMELINE_STEP) {
      return undefined;
    }

    const currentDuration = STEP_DURATIONS[currentStep] ?? 220;

    const timer = window.setTimeout(() => {
      setCurrentStep((previousStep) =>
        Math.min(previousStep + 1, LAST_TIMELINE_STEP),
      );
    }, currentDuration);

    return () => {
      window.clearTimeout(timer);
    };
  }, [currentStep, hasEntered]);

  /*
   * Al cambiar de idioma:
   * reiniciamos solamente la secuencia del contenido.
   */
  useEffect(() => {
    setCurrentStep(0);
  }, [i18n.language]);

  return (
    <section
      ref={sectionRef}
      className="home-wpp-hero"
      aria-labelledby="home-wpp-hero-title"
      aria-label={t("accessibility.sectionLabel")}
    >
      <div className="home-wpp-hero__glow home-wpp-hero__glow--one" />
      <div className="home-wpp-hero__glow home-wpp-hero__glow--two" />

      <div className="home-wpp-hero__inner">
        <div className="home-wpp-hero__content">
          <span className="home-wpp-hero__eyebrow">{t("hero.eyebrow")}</span>

          <h1 id="home-wpp-hero-title">
            {t("hero.title.before")}{" "}
            <span className="home-wpp-hero__highlight">
              {t("hero.title.highlight")}
            </span>
            <br />
            {t("hero.title.after")}
          </h1>

          <p>{t("hero.description")}</p>
        </div>

        <div className="home-wpp-hero__visual">
          <div className="home-wpp-hero__phone-wrapper">
            <ZYBuddyPhone
              currentStep={currentStep}
              timelineMessages={timelineMessages}
              assistantName="ZY Buddy"
              onlineLabel={t("phone.online")}
              backLabel={t("conversationFeature.phone.backLabel", {
                defaultValue: "Volver",
              })}
              viewButtonLabel={t("phone.viewButton")}
              phoneLabel={t("accessibility.phoneLabel")}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
